import React from 'react';
import { Button as PrimeButton } from 'primereact/button';

/**
 * PrimeReact 8.7.3 compatibility shim for the v9 Button visual props.
 *
 * The codebase uses the PrimeReact v9 prop API (`outlined`, `text`, `severity`,
 * `size`, `raised`, `rounded`, `link`, `plain`), but the installed PrimeReact is
 * v8.7.3 whose Button DOES NOT support them — it builds its className from only
 * `'p-button p-component' + props.className + {icon-only,vertical,disabled,loading}`
 * (see node_modules/primereact/button/button.cjs.js). The extra props fall into
 * `otherProps` and are spread onto the DOM <button>; React then DROPS the boolean
 * ones entirely, so e.g. `<Button outlined={!selected}>` adds NO class and
 * selected/unselected toggles render identically.
 *
 * This shim maps those props to the exact classNames v8's theme already styles
 * (and which pp-overrides.css further tunes), so every `<Button outlined|text|
 * severity|size|link|plain …>` renders as intended. Everything else passes
 * through. This is now the ONE canonical Button import for the whole app — see
 * the `codemod-button-imports` note in git history for the migration off the
 * raw `primereact/button` import.
 */
const SEVERITIES = ['secondary', 'success', 'info', 'warning', 'help', 'danger', 'contrast'];
const SIZE_CLASS = { small: 'p-button-sm', large: 'p-button-lg' };

export const Button = React.forwardRef(function Button(
  { outlined, text, raised, rounded, link, plain, severity, size, className, ...rest },
  ref,
) {
  const cls = [
    className,
    outlined && 'p-button-outlined',
    text && 'p-button-text',
    raised && 'p-button-raised',
    rounded && 'p-button-rounded',
    link && 'p-button-link',
    plain && 'p-button-plain',
    SEVERITIES.includes(severity) && `p-button-${severity}`,
    SIZE_CLASS[size],
  ].filter(Boolean).join(' ') || undefined;

  return <PrimeButton ref={ref} className={cls} {...rest} />;
});

export default Button;
